import {ComponentType, SVGProps} from "react";
import type {TFunction} from "i18next";
import nb from "../../locale/nb.json";
import IconListCheck from "../../assets/list-check.svg?react";
import IconBook from "../../assets/book.svg?react";
import IconNews from "../../assets/news.svg?react";
import IconGavel from "../../assets/gavel.svg?react";
import IconTable from "../../assets/table.svg?react";
import IconInfoCircle from "../../assets/info-circle.svg?react";

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

type LinkTypeMeta = {
    Icon: IconComponent;
    label: string;
    labelKey: string;
};

const URL_SEGMENT_META: Record<string, LinkTypeMeta> = {
    "sjekkliste-maler": {Icon: IconListCheck, label: "Sjekkliste / Maler", labelKey: "Type.ChecklistTemplates"},
    dokument: {Icon: IconListCheck, label: "Dokumentmal", labelKey: "Type.Template"},
    revisormal: {Icon: IconListCheck, label: "Revisormal", labelKey: "Type.AuditTemplate"},

    portal: {Icon: IconNews, label: "Portal", labelKey: "Type.PortalReference"},
    nyhet: {Icon: IconNews, label: "Nyhet", labelKey: "Type.News"},

    faglitteratur: {Icon: IconBook, label: "Faglitteratur", labelKey: "Type.Literature"},
    "skatte-abc": {Icon: IconBook, label: "Skatte-ABC", labelKey: "Type.SkatteABC"},
    praktika: {Icon: IconBook, label: "Praktika", labelKey: "Type.Praktika"},
    tema: {Icon: IconBook, label: "Tema", labelKey: "Type.Topic"},
    art: {Icon: IconBook, label: "Art", labelKey: "Type.Nature"},
    funksjon: {Icon: IconBook, label: "Funksjon", labelKey: "Type.Function"},
    qna: {Icon: IconBook, label: "Q&A", labelKey: "Type.Q&A"},
    kontoplan: {Icon: IconBook, label: "Kontoplan", labelKey: "Type.ChartOfAccounts"},

    haandbok: {Icon: IconGavel, label: "Håndbok", labelKey: "Type.Handbook"},
    standard: {Icon: IconGavel, label: "Standard", labelKey: "Type.Standard"},
    lovkommentar: {Icon: IconGavel, label: "Lovkommentar", labelKey: "Type.LegalCommentary"},
    lov: {Icon: IconGavel, label: "Lov", labelKey: "Type.Law"},
    forskrift: {Icon: IconGavel, label: "Forskrift", labelKey: "Type.Regulation"},
    direktiv: {Icon: IconGavel, label: "Direktiv", labelKey: "Type.Directive"},
    forarbeid: {Icon: IconGavel, label: "Forarbeid", labelKey: "Type.PreparatoryWorks"},
    skatteavtale: {Icon: IconGavel, label: "Skatteavtale", labelKey: "Type.TaxTreaty"},
    "artikkel-uttalelse": {Icon: IconGavel, label: "Uttalelse", labelKey: "Type.Statement"},
    avgjoerelse: {Icon: IconGavel, label: "Avgjørelse", labelKey: "Type.Decision"},
    "juridisk-vejledning": {Icon: IconGavel, label: "Juridisk Vejledning", labelKey: "Type.JuridiskVejledning"},

    "regneark-regnskap": {Icon: IconTable, label: "Regneark / Regnskap", labelKey: "Type.Spreadsheet"},
};

const UNKNOWN: LinkTypeMeta = {Icon: IconInfoCircle, label: "Annet", labelKey: "Type.Unknown"};

function extractTypeSegment(urlOrPath: string): string | null {
    try {
        const path = urlOrPath.startsWith("http")
            ? new URL(urlOrPath).pathname
            : urlOrPath;
        const segments = path.split("/").filter(Boolean);
        return segments[0] ?? null;
    } catch {
        return null;
    }
}

export function getLinkTypeMeta(
    urlOrPath: string | null | undefined
): LinkTypeMeta {
    if (!urlOrPath) return UNKNOWN;
    const segment = extractTypeSegment(urlOrPath);
    return (segment && URL_SEGMENT_META[segment]) || UNKNOWN;
}

export function getLinkTypeSlug(
    urlOrPath: string | null | undefined
): string | null {
    if (!urlOrPath) return null;
    return extractTypeSegment(urlOrPath);
}

// Ported from DibApp2020's slider-relations: find the Lokalise key for a
// type name by scanning nb.json values, so the label can be translated.
export const getLangKeyFromString = (value: string) =>
    Object.entries(nb).find(([, v]) => v === value)?.[0];

export function resolveTypeLabel(
    t: TFunction,
    topicType: string | null | undefined,
    urlOrPath: string | null | undefined
): string {
    if (topicType) {
        const key = getLangKeyFromString(topicType);
        if (key) return t(key, {defaultValue: topicType});
    }
    const meta = getLinkTypeMeta(urlOrPath);
    if (meta !== UNKNOWN || !topicType) {
        return t(meta.labelKey, {defaultValue: meta.label});
    }
    return topicType;
}
